import { useRef, useState, type PointerEvent } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { moveAnnotation, type RefuseAnnotation } from '../../services/signature/refuseAnnotations';

interface Props {
  annotations: RefuseAnnotation[];
  page: number;
  canEdit: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onDelete: (id: string) => void;
  onMove?: (id: string, x: number, y: number) => void;
}

export function RefuseAnnotationMarks({
  annotations, page, canEdit, selectedId, onSelect, onDelete, onMove,
}: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const metaRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    pw: number;
    ph: number;
    moved: boolean;
  } | null>(null);

  function beginDrag(e: PointerEvent<HTMLElement>, a: RefuseAnnotation) {
    onSelect?.(a.id);
    if (!canEdit || !onMove) return;
    if ((e.target as HTMLElement).closest('button[aria-label^="Retirer"]')) return;
    const pageEl = (e.currentTarget as HTMLElement).offsetParent as HTMLElement | null;
    const r = pageEl?.getBoundingClientRect();
    if (!r || r.width <= 0 || r.height <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    metaRef.current = {
      id: a.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: a.x,
      origY: a.y,
      pw: r.width,
      ph: r.height,
      moved: false,
    };
  }

  function onPointerMove(e: PointerEvent<HTMLElement>, a: RefuseAnnotation) {
    const meta = metaRef.current;
    if (!meta || meta.id !== a.id) return;
    const nx = meta.origX + (e.clientX - meta.startX) / meta.pw;
    const ny = meta.origY + (e.clientY - meta.startY) / meta.ph;
    if (Math.abs(nx - meta.origX) + Math.abs(ny - meta.origY) > 0.008) meta.moved = true;
    const next = moveAnnotation(a, nx, ny);
    setDrag({ id: a.id, x: next.x, y: next.y });
  }

  function onPointerUp(e: PointerEvent<HTMLElement>, a: RefuseAnnotation) {
    e.stopPropagation();
    const meta = metaRef.current;
    metaRef.current = null;
    if (meta?.moved && onMove) {
      const nx = meta.origX + (e.clientX - meta.startX) / meta.pw;
      const ny = meta.origY + (e.clientY - meta.startY) / meta.ph;
      const next = moveAnnotation(a, nx, ny);
      onMove(a.id, next.x, next.y);
    } else if (a.kind === 'highlight') {
      setOpenId(a.text ? a.id : null);
    } else {
      setOpenId(openId === a.id ? null : a.id);
    }
    setDrag(null);
  }

  return (
    <>
      {annotations.filter((a) => a.page === page).map((a) => {
        const shown = drag?.id === a.id ? { ...a, x: drag.x, y: drag.y } : a;
        const selected = selectedId === a.id;
        const open = openId === a.id && Boolean(a.text);

        if (a.kind === 'highlight') {
          return (
            <div
              key={a.id}
              className={`absolute bg-amber-400/40 border pointer-events-auto ${
                canEdit ? 'cursor-grab' : 'cursor-pointer'
              } ${selected ? 'border-amber-700 ring-2 ring-amber-400' : 'border-amber-500/60'}`}
              style={{
                left: `${shown.x * 100}%`,
                top: `${shown.y * 100}%`,
                width: `${shown.w * 100}%`,
                height: `${shown.h * 100}%`,
              }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => {
                e.stopPropagation();
                beginDrag(e, a);
              }}
              onPointerMove={(e) => onPointerMove(e, a)}
              onPointerUp={(e) => onPointerUp(e, a)}
            >
              {a.text && (
                <span className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center shadow pointer-events-none">
                  <MessageSquare className="w-3 h-3" />
                </span>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="absolute -top-2 -right-2 z-10 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center"
                  aria-label="Retirer le surlignage"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(a.id);
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              {open && (
                <div
                  className="absolute left-0 top-full mt-1 z-30 w-56 max-w-[70vw] bg-amber-50 border border-amber-400 rounded-md shadow-md p-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-xs text-amber-950 whitespace-pre-wrap">{a.text}</p>
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            key={a.id}
            className={`absolute z-20 ${canEdit ? 'cursor-grab' : ''}`}
            style={{ left: `${shown.x * 100}%`, top: `${shown.y * 100}%` }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => {
              e.stopPropagation();
              beginDrag(e, a);
            }}
            onPointerMove={(e) => onPointerMove(e, a)}
            onPointerUp={(e) => onPointerUp(e, a)}
          >
            <span
              className={`w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center shadow pointer-events-none ${
                selected ? 'ring-2 ring-amber-700' : ''
              }`}
              aria-label="Lire la note"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </span>
            {canEdit && (
              <button
                type="button"
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center"
                aria-label="Retirer la note"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(a.id);
                }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {open && (
              <div
                className="absolute left-7 top-0 z-30 w-56 max-w-[70vw] bg-amber-50 border border-amber-400 rounded-md shadow-md p-2"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-xs text-amber-950 whitespace-pre-wrap">{a.text}</p>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
